import CitySEOHead from "@/components/feature/CitySEOHead";
import StGeorgeHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";
import SharedProcessingTimeSection from "@/components/feature/SharedProcessingTimeSection";

const stGeorgeProcessingOptions = [
  {
    speed: "24",
    unit: "Hours*",
    label: "SLC Passport Agency",
    tag: "Emergency Only",
    tagColor: "bg-gray-100 text-gray-500",
    features: [
      "Appointment required — no walk-ins accepted",
      "Life-or-death emergencies only",
      "Bereavement or medical crisis",
      "300 miles north — 4.5-hour drive one way",
      "9-hour round trip — not practical for most families",
    ],
    highlight: false,
    emergency: true,
  },
  {
    speed: "2–9",
    unit: "Business Days",
    label: "Fast Passport Center",
    tag: "Most Popular",
    tagColor: "bg-[#F6C344] text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Predictable turnaround — no guessing",
      "Ideal for retirees, Utah Tech students, outdoor travelers",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: true,
  },
  {
    speed: "3–5",
    unit: "Weeks",
    label: "Fast Passport Center",
    tag: "Standard Expedited",
    tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Faster than self-service",
      "Good for planned travel — cruises, retirement trips, family vacations",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: false,
    smartSaver: true,
  },
  {
    speed: "1–2",
    unit: "Months",
    label: "Self-Service (Post Office)",
    tag: "Slowest",
    tagColor: "bg-gray-100 text-gray-400",
    features: [
      "No courier access — standard mail only",
      "St. George, Hurricane & Cedar City post offices book weeks out",
      "In-person clerk appointment required",
      "No document review — errors cause rejection",
      "No dedicated agent — you are on your own",
      "Updates only by physical mail letter",
      "No phone or email contact for your case",
      "Can't upgrade speed after submission",
      "Not suitable if travel is within 10 weeks",
    ],
    highlight: false,
    muted: true,
  },
];

export const faqs = [
  {
    q: "Where is the nearest passport agency to St. George?",
    a: "The nearest regional passport agency is the Salt Lake City Passport Agency at 324 S State St, Suite 225 — approximately 300 miles north of St. George. That's roughly a 4.5-hour drive. Our courier service eliminates the need for that trip entirely.",
  },
  {
    q: "How fast can I get a passport in St. George, Utah?",
    a: "Through our expedited courier service, passports can be processed as fast as 24 hours for emergencies. Rush service takes 3-5 business days. Standard expedited takes 5-7 days. We understand the unique geographic isolation of Southern Utah.",
  },
  {
    q: "Do I need to drive to Salt Lake City for a passport?",
    a: "Not with our service. Everything is handled by mail. You send us your documents via FedEx with our prepaid label, and we courier them directly to the State Department in Washington, D.C. Your completed passport is delivered back to your St. George address.",
  },
  {
    q: "I'm a Dixie State University student — can you help with my study abroad?",
    a: "Absolutely. We regularly help DSU students with study abroad passports. We understand the unique timing of Southern Utah University programs and our expedited service handles 2-4 week windows comfortably.",
  },
  {
    q: "Can retirees in Southern Utah get expedited passports?",
    a: "Yes. Our service is popular with St. George and SunRiver retirees who cruise the Mediterranean or visit New Zealand. The process is entirely by mail — no driving, no standing in line.",
  },
  {
    q: "What post offices accept passport applications in St. George?",
    a: "The St. George Main Post Office at 315 E Tabernacle St accepts passport applications. Other nearby options include Hurricane Post Office (98 S Main St, Hurricane) and Washington Post Office (1320 W Telegraph St, Washington). Appointments are recommended.",
  },
  {
    q: "How do I avoid the common St. George passport mistakes?",
    a: "The most common mistakes are: underestimating the 300-mile distance to the SLC agency, not checking 6-month validity rules for your destination, and waiting for post office appointments that are weeks out. We review all documents before submission.",
  },
  {
    q: "Is your service more affordable than a road trip to the SLC agency?",
    a: "For most travelers, yes. Driving to Salt Lake City costs gas, time, and a full day of work. Our courier service is typically 30-100% less than major resellers and you never leave your home.",
  },
];

export default function StGeorgePage() {
  return (
    <>
      <CitySEOHead
        city="St. George"
        state="Utah"
        stateAbbr="UT"
        route="/st-george"
        title="Emergency Passport St. George, UT — Expedited & Fast Passport Service | Fast Passport Center"
        description="Emergency and expedited passport services in St. George, UT — as fast as 24 hours. Serving Southern Utah retirees, Dixie State University students, outdoor recreation enthusiasts, and Washington County families. No drive to Salt Lake City required."
        geoPlacename="St. George, Utah"
        geoPosition="37.0965;-113.5684"
        faqs={faqs}
        servicesSchema={[
          { name: "Emergency Passport St George UT", description: "Urgent passport processing for St. George Utah travelers with immediate departure needs — no 300-mile drive to Salt Lake City required." },
          { name: "Expedited Passport Renewal St George UT", description: "Fast passport renewal for St. George Utah residents with a previously-issued passport." },
          { name: "First-Time Passport St George UT", description: "New passport application services for St. George Utah and Washington County residents applying for their first passport." },
          { name: "Child Passport St George UT", description: "Minor passport services for St. George Utah families with children under 16." },
          { name: "Name Change Passport St George UT", description: "Passport name correction after marriage, divorce, or legal name changes for St. George residents." },
          { name: "Lost Stolen Passport St George UT", description: "Replacement passport service for St. George residents with lost or stolen travel documents, including DS-64 guidance." },
          { name: "Second Passport St George UT", description: "Second passport services for St. George Utah retirees and frequent international travelers." },
        ]}
        keywords="emergency passport St. George, expedited passport Southern Utah, St. George passport renewal, passport service Utah, same day passport St. George"
        statePageName="Utah"
        statePageRoute="/states/utah"
      />
      <div className="font-sans">
        <StGeorgeHeroSection />

        <div className="bg-[#1A365D] text-white sticky top-0 z-40 hidden lg:block">
          <div className="max-w-7xl mx-auto px-8">
            <div className="flex items-center gap-8 h-12 overflow-x-auto">
              {[
                { label: "Processing", href: "#processing" },
                { label: "Services", href: "#services" },
                { label: "Local Area", href: "#local-area" },
                { label: "Travel Timing", href: "#travel-timing" },
                { label: "Testimonials", href: "#testimonials" },
                { label: "Facilities", href: "#facilities" },
                { label: "Compare", href: "#compare" },
                { label: "How It Works", href: "#how-it-works" },
                { label: "FAQ", href: "#faq" },
              ].map((item) => (
                <a key={item.href} href={item.href} className="text-sm text-white/80 hover:text-[#F6C344] whitespace-nowrap transition-colors py-3 border-b-2 border-transparent hover:border-[#F6C344]">
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        <SharedProcessingTimeSection
          sectionId="processing"
          cityName="St. George"
          headingExtra="UT"
          introText="The SLC Passport Agency is 300 miles north — a 4.5-hour drive one way. Appointments book out weeks in advance and walk-ins are only for verified life-or-death emergencies. Our registered courier service hand-delivers your application daily, so Southern Utah retirees, Utah Tech students, outdoor recreation travelers, and Washington County families get their passports on a predictable timeline — no 9-hour round trip, no appointment lottery, no waiting room."
          options={stGeorgeProcessingOptions}
          emergencyDescription="The SLC Passport Agency at 324 S State St, 300 miles north of St. George (4.5-hour drive), serves verified life-or-death emergencies only. You must call NPIC (877-487-2778) and have proof of imminent international travel within 72 hours. Appointments are extremely limited and not guaranteed — and the 9-hour round trip makes this impractical for most Southern Utah families."
          disclaimerText="* Emergency processing availability depends on U.S. Department of State volume, appointment slots, and your specific circumstances. Travel must be within 72 hours for agency emergency appointments. Courier processing times are based on business days and are not guaranteed — but our daily submission schedule gives St. George and Southern Utah residents the fastest legally available route without the 300-mile drive."
          afterCardsContent={
            <div className="mt-16">
              <div className="text-center mb-10">
                <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-4">Where St. George Travelers Are Going</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {[
                  { city: "Rome, Italy", flag: "🇮🇹", note: "Mediterranean cruise" },
                  { city: "Auckland, New Zealand", flag: "🇳🇿", note: "Adventure travel" },
                  { city: "Cancun, Mexico", flag: "🇲🇽", note: "Family vacation" },
                  { city: "Barcelona, Spain", flag: "🇪🇸", note: "Mediterranean cruise" },
                  { city: "London, UK", flag: "🇬🇧", note: "Study abroad" },
                  { city: "Patagonia, Argentina", flag: "🇦🇷", note: "Outdoor adventure" },
                ].map((dest) => (
                  <div key={dest.city} className="flex items-center gap-3 bg-[#F7F8FA] rounded-lg p-4">
                    <span className="text-2xl">{dest.flag}</span>
                    <div>
                      <p className="font-bold text-[#1A365D] text-sm">{dest.city}</p>
                      <p className="text-gray-500 text-xs">{dest.note}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { stat: "300 mi", label: "Distance to SLC agency" },
                  { stat: "24 hrs", label: "Fastest turnaround" },
                  { stat: "20+ yrs", label: "Serving Utah families" },
                  { stat: "4.9★", label: "Customer rating" },
                ].map((s) => (
                  <div key={s.label} className="text-center bg-[#EEF2F8] rounded-lg p-5">
                    <p className="font-bold text-[#1A365D] text-2xl mb-1">{s.stat}</p>
                    <p className="text-gray-600 text-sm">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          }
        />

        <section id="services" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">What We Handle</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for St. George &amp; Southern Utah</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: "ri-passport-line", title: "Passport Renewal", desc: "Adult renewals by mail with no post office visit. Perfect for retirees in SunRiver and Entrada who don't want to drive." },
                { icon: "ri-user-add-line", title: "First-Time Passport", desc: "Complete DS-11 guidance and acceptance facility support for first-time applicants throughout Washington County." },
                { icon: "ri-parent-line", title: "Child Passport", desc: "Minor under 16? Both parents must consent. We guide you through the DS-11 minor requirements and acceptance clerk appointment." },
                { icon: "ri-alert-line", title: "Lost or Stolen Passport", desc: "DS-64 filing plus replacement application. We handle the paperwork and the courier leg together." },
                { icon: "ri-file-edit-line", title: "Name Change Passport", desc: "Post-marriage or legal name change? We process DS-5504 corrections and full renewals." },
                { icon: "ri-global-line", title: "Second Passport", desc: "Required for frequent travelers visiting countries with conflicting visa policies. We handle this specialized request." },
              ].map((svc) => (
                <div key={svc.title} className="bg-white rounded-lg p-7 border border-gray-100">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                    <i className={`${svc.icon} text-[#1A365D] text-2xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-lg mb-2">{svc.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{svc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="local-area" className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">St. George Context</p>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-6">Why St. George Residents Need Expedited Passport Help</h2>
                <div className="text-gray-600 text-sm leading-relaxed space-y-4">
                  <p>St. George sits at the extreme southern edge of Utah, closer to Las Vegas than to Salt Lake City. With the regional passport agency located 300 miles north in Salt Lake City, St. George residents face the most significant geographic barrier to passport services of any major city in Utah. A round trip to the SLC agency takes 9 hours and a full tank of gas — plus the agency's appointment windows are notoriously difficult to secure during peak seasons. For a city of 100,000+ residents, this isolation creates real passport accessibility challenges that our courier service solves entirely.</p>
                  <p>The St. George population skews older than most Utah cities. With its warm climate, golf courses, and proximity to Zion National Park, the city attracts retirees from across the northern United States and the Midwest. These retirees are prolific international travelers — Mediterranean cruises, New Zealand tours, European river cruises, and Southeast Asian adventure trips. Many don't realize their passport expired until they attempt to book a cruise or confirm their flight. A 10-12 week standard postal timeline simply doesn't work when your cruise departs from Barcelona in 3 weeks.</p>
                  <p>Utah Tech University (formerly Dixie State University) adds another dimension of demand. Its 10,000+ students participate in outdoor recreation leadership programs, international service-learning trips, and academic exchanges that require passports on short notice. The university's outdoor recreation and tourism programs specifically attract students interested in international adventure travel — the exact demographic most likely to need expedited passport services. Our courier service removes the 300-mile barrier entirely: you send your documents from St. George, Hurricane, or Cedar City, and we handle the rest via Washington, D.C.</p>
                </div>
              </div>
              <div className="space-y-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Dramatic%20red%20rock%20landscape%20of%20St.%20George%20Utah%20with%20Utah%20Tech%20University%20campus%20in%20the%20foreground%2C%20iconic%20red%20sandstone%20formations%20and%20desert%20mesas%20in%20the%20background%2C%20warm%20southwestern%20desert%20light%2C%20red%20and%20orange%20tones%20with%20sagebrush%20vegetation%2C%20clear%20blue%20sky%20with%20dramatic%20clouds%2C%20professional%20landscape%20photography%2C%20no%20readable%20text%20or%20signs&width=700&height=450&seq=stgeorge-redrocks&orientation=landscape"
                  alt="St. George Utah red rock landscape"
                  className="w-full h-[250px] object-cover rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "Distance to SLC Agency", value: "300 miles (4.5 hrs)" },
                    { label: "Utah Tech Enrollment", value: "10,000+ students" },
                    { label: "Nearest Airport", value: "St. George Regional (SGU)" },
                    { label: "Key Demographics", value: "Retirees, students, outdoor rec" },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-[#F7F8FA] rounded-lg p-4">
                      <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                      <p className="font-bold text-[#1A365D] text-sm">{stat.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="travel-timing" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">When to Apply</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">St. George Passport Timing Guide</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { scenario: "Retiree International Cruise", window: "Apply 6-8 weeks before cruise", urgency: "Low-Medium", icon: "ri-ship-line", detail: "Mediterranean and Alaska cruises book months out. Standard expedited works, but don't wait past 6 weeks." },
                { scenario: "Utah Tech Study Abroad", window: "Apply 3-4 weeks before departure", urgency: "Medium", icon: "ri-graduation-cap-line", detail: "Outdoor recreation and service-learning programs need passports. Apply as soon as you're accepted." },
                { scenario: "Outdoor Adventure Trip", window: "Apply as soon as trip is booked", urgency: "Variable", icon: "ri-landscape-line", detail: "Patagonia, New Zealand, and Nepal trips are popular. Many require 6+ months of validity." },
                { scenario: "Family Vacation Overseas", window: "Apply 4-6 weeks before departure", urgency: "Medium", icon: "ri-plane-line", detail: "Summer European trips and winter Caribbean getaways. Use our rush or expedited service." },
              ].map((item) => (
                <div key={item.scenario} className="bg-white rounded-lg p-6 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                    <i className={`${item.icon} text-[#1A365D] text-lg`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-1">{item.scenario}</h3>
                  <p className="text-[#F6C344] text-xs font-bold mb-2 uppercase">{item.window}</p>
                  <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-3 ${item.urgency === "High" ? "bg-red-100 text-red-700" : item.urgency === "Medium" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{item.urgency} Urgency</span>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>



        <section id="testimonials" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Real Stories</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">St. George &amp; Southern Utah Clients</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: "Robert H.", location: "SunRiver, St. George", text: "Booked a Mediterranean cruise and realized my passport expired. With the agency 300 miles away, I thought I was stuck. They had my renewal in 6 days. Never even left my house.", stars: 5 },
                { name: "Mackenzie B.", location: "Washington, UT", text: "Utah Tech study abroad to New Zealand. Passport was fine but my name was wrong after I got married. Fixed and returned in 5 days. Incredible.", stars: 5 },
                { name: "Linda W.", location: "Hurricane, UT", text: "Grandkids wanted to take me on a cruise. First passport ever at 67. They walked me through the whole process and I had it in 8 days. No drive to Salt Lake.", stars: 5 },
              ].map((t) => (
                <div key={t.name} className="bg-white rounded-lg p-7 border border-gray-100">
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#F6C344] text-sm"></i>)}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-5 italic">"{t.text}"</p>
                  <div>
                    <p className="font-bold text-[#1A365D] text-sm">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="facilities" className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Offices</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Acceptance Facilities Near St. George</h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-sm">First-time applicants and minors must visit an acceptance facility before our courier process begins.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: "St. George Main Post Office", address: "315 E Tabernacle St, St. George", hours: "Mon-Fri 9am-5pm", type: "Acceptance Facility" },
                { name: "Hurricane Post Office", address: "98 S Main St, Hurricane", hours: "Mon-Fri 9am-5pm", type: "Acceptance Facility" },
                { name: "Washington Post Office", address: "1320 W Telegraph St, Washington", hours: "Mon-Fri 9am-5pm", type: "Acceptance Facility" },
                { name: "Cedar City Post Office", address: "333 N Main St, Cedar City", hours: "Mon-Fri 9am-5pm", type: "Acceptance Facility" },
                { name: "SLC Passport Agency", address: "324 S State St, Suite 225, SLC", hours: "Mon-Fri 8am-3pm (by appt)", type: "Regional Agency (300 mi)" },
              ].map((fac) => (
                <div key={fac.name} className="border border-gray-200 rounded-lg p-6">
                  <span className="inline-block bg-[#EEF2F8] text-[#1A365D] text-xs font-semibold px-2 py-0.5 rounded-full mb-3">{fac.type}</span>
                  <h3 className="font-bold text-[#1A365D] text-base mb-1">{fac.name}</h3>
                  <p className="text-gray-600 text-sm mb-1"><i className="ri-map-pin-line mr-1.5"></i>{fac.address}</p>
                  <p className="text-gray-500 text-xs"><i className="ri-time-line mr-1.5"></i>{fac.hours}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="compare" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Why Choose Us</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. Alternatives</h2>
            </div>

            <div className="bg-amber-50 border border-[#F6C344]/40 rounded-lg p-5 md:p-6 mb-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-bold text-base text-[#1A365D] mb-1">Passport Agency Appointment Not Available?</h3>
                  <p className="text-sm text-gray-600">Don't let a booked-out agency appointment block your travel plans. We bypass the agency bottleneck with reserved daily courier slots — faster and no appointment roulette.</p>
                </div>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Get Started <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A365D] text-white">
                    <th className="text-left px-4 py-3 font-semibold rounded-tl-lg">Feature</th>
                    <th className="text-center px-4 py-3 font-semibold text-[#F6C344]">Fast Passport Center</th>
                    <th className="text-center px-4 py-3 font-semibold">SLC Agency (300 mi)</th>
                    <th className="text-center px-4 py-3 font-semibold rounded-tr-lg">FedEx / UPS</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["No drive to Salt Lake City", "✓", "✗", "✗"],
                    ["As fast as 24 hours", "✓", "Same-day only", "✗"],
                    ["Price vs. resellers", "30-100% less", "Govt fee only", "Full markup"],
                    ["Registered State Dept. courier", "✓", "N/A", "✗"],
                    ["Retiree-friendly (no driving)", "✓", "✗", "✗"],
                    ["Document error review", "✓", "✗", "✗"],
                    ["Handles family applications", "✓", "✗", "✗"],
                    ["FedEx delivery to your door", "✓", "Pick up in person", "Partial"],
                    ["A+ BBB rated", "✓", "N/A", "Varies"],
                  ].map(([feat, us, agency, fedex], i) => (
                    <tr key={feat} className={i % 2 === 0 ? "bg-white" : "bg-[#F7F8FA]"}>
                      <td className="px-4 py-3 text-gray-700 font-medium">{feat}</td>
                      <td className="px-4 py-3 text-center text-green-600 font-semibold">{us}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{agency}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{fedex}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common St. George Passport Mistakes</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "Not realizing how far the SLC agency is", desc: "300 miles is 4.5 hours each way. Don't plan a day trip. Our mail-based service is the practical alternative." },
                { title: "Waiting for local post office appointments", desc: "St. George, Hurricane, and Cedar City post offices book acceptance slots 2-4 weeks out in spring. Apply online immediately." },
                { title: "Missing the 6-month validity rule", desc: "Many European cruise countries and New Zealand require 6 months validity beyond travel date. Check early." },
                { title: "Wrong photo background or dimensions", desc: "White background, 2x2 inches, no glasses. We review all photos before courier dispatch." },
                { title: "Forgetting to apply for a cruise group", desc: "Multi-generational cruise families often need 5-7 passports. We process them all together." },
                { title: "Not realizing the Las Vegas agency is also far", desc: "The Las Vegas agency is 2 hours away and also requires proof of imminent departure. Use our mail service instead." },
              ].map((m) => (
                <div key={m.title} className="flex gap-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0 mt-0.5">
                    <i className="ri-close-circle-line text-red-600 text-base"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A365D] text-sm mb-1">{m.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">The Process</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for St. George Residents</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: "1", title: "Order Online", desc: "Select your service and complete our secure order. 5 minutes, no paperwork to start." },
                { step: "2", title: "Mail Documents", desc: "Use our prepaid FedEx label from any drop location in St. George, Hurricane, or Washington." },
                { step: "3", title: "We Courier to D.C.", desc: "Our registered courier hand-delivers your application to the State Department in Washington, D.C." },
                { step: "4", title: "Delivered to Your Door", desc: "Your completed passport ships via FedEx Priority Overnight to your St. George area address." },
              ].map((s) => (
                <div key={s.step} className="text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-xl mx-auto mb-4">{s.step}</div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="bg-white py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Questions</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">St. George Passport FAQs</h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="border border-gray-200 rounded-lg group">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <h4 className="font-semibold text-[#1A365D] text-sm pr-4">{faq.q}</h4>
                    <i className="ri-add-line text-[#1A365D] flex-shrink-0 group-open:hidden"></i>
                    <i className="ri-subtract-line text-[#1A365D] flex-shrink-0 hidden group-open:block"></i>
                  </summary>
                  <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Official Links</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for St. George Passport Applicants</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { title: "U.S. State Dept. Passport Info", url: "https://travel.state.gov", icon: "ri-government-line" },
                { title: "SLC Passport Agency (300 mi north)", url: "https://travel.state.gov/content/travel/en/passports/get-fast/passport-agencies.html", icon: "ri-building-line" },
                { title: "Utah Tech University (formerly DSU)", url: "https://www.utahtech.edu", icon: "ri-graduation-cap-line" },
                { title: "DS-82 Renewal Form", url: "https://travel.state.gov/content/travel/en/passports/need-passport/ds-82.html", icon: "ri-file-text-line" },
                { title: "St. George Regional Airport (SGU)", url: "https://www.iflysgu.com", icon: "ri-flight-takeoff-line" },
                { title: "Utah DMV (ID Documentation)", url: "https://dld.utah.gov", icon: "ri-id-card-line" },
              ].map((r) => (
                <a key={r.title} href={r.url} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-4 bg-white rounded-lg p-5 border border-gray-200 hover:border-[#F6C344] transition-colors group">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                    <i className={`${r.icon} text-[#1A365D] text-lg`}></i>
                  </div>
                  <span className="text-[#1A365D] font-medium text-sm group-hover:text-[#F6C344] transition-colors">{r.title}</span>
                  <i className="ri-external-link-line text-gray-400 ml-auto"></i>
                </a>
              ))}
            </div>
          </div>
        </section>

        <SEOCrossLinks currentRoute="/cities/st-george" state="UT" />

        <section className="bg-[#1A365D] py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-4">Get Started Today</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">St. George's Passport Service — No Drive to Salt Lake City</h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">300 miles to the nearest agency shouldn't be a barrier. We bring the passport agency to your mailbox. As fast as 24 hours, delivered to your door.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center justify-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
                Start My Passport <i className="ri-arrow-right-line"></i>
              </a>
              <a href="tel:8772530084" className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
                <i className="ri-phone-line"></i>877-253-0084
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}